// TASK-042 — test-fire button + result chip. The button invokes the
// non-mutating preview endpoint and renders a "would fire / would not
// fire" verdict next to itself. Disabled rules render a hint instead.

import type { AlertRuleResponse } from "@community-analytics/shared";

import {
  TEST_FIRE_REASON_LABELS,
  useTestFireAlertRuleMutation,
} from "@entities/alert-rule";

import { Button } from "@shared/ui";
import { cn } from "@shared/lib";

export interface AlertTestFireButtonProps {
  communityId: string | null;
  rule: AlertRuleResponse;
}

export function AlertTestFireButton({
  communityId,
  rule,
}: AlertTestFireButtonProps) {
  const mutation = useTestFireAlertRuleMutation(communityId);
  const disabled =
    communityId === null || mutation.isPending || !rule.enabled;
  const result = mutation.data ?? null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => {
          mutation.mutate(rule.id);
        }}
        aria-label={`Test-fire alert ${rule.type}`}
      >
        Test-fire
      </Button>

      {!rule.enabled ? (
        <span className="text-xs text-muted-foreground">
          Enable the rule to run a test-fire.
        </span>
      ) : null}

      {mutation.isError ? (
        <span role="alert" className="text-xs text-destructive">
          Test-fire failed. Try again.
        </span>
      ) : null}

      {result ? (
        <span
          role="status"
          className={cn(
            "rounded-md px-2 py-1 text-xs font-medium tabular-nums",
            result.wouldFire
              ? "bg-destructive/10 text-destructive"
              : "bg-muted text-muted-foreground",
          )}
        >
          {TEST_FIRE_REASON_LABELS[result.reason]}
          {result.metricValue !== null ? (
            <span className="ml-1 opacity-70">
              ({formatMetricSummary(result)})
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

function formatMetricSummary(result: {
  type: AlertRuleResponse["type"];
  threshold: number;
  metricValue: number | null;
  referenceValue: number | null;
}): string {
  if (result.metricValue === null) return "";
  switch (result.type) {
    case "HEALTH_DROP":
      return `score ${Math.round(result.metricValue)} vs ref ${
        result.referenceValue !== null
          ? Math.round(result.referenceValue)
          : "?"
      }`;
    case "MEMBER_DROP":
      return `lost ${(result.metricValue * 100).toFixed(1)}% in 24h`;
    case "ACTIVITY_SPIKE":
      return `${result.metricValue} today vs median ${
        result.referenceValue !== null ? result.referenceValue : "?"
      }`;
  }
}
